/**
 * Infos Tab Component
 *
 * Displays and allows editing of general product information.
 */

import { useState } from 'react';
import { EditableField } from '@/components/ui';
import { EditableSelect } from '@/components/ui';
import { Button, Modal, ModalFooter } from '@/components/ui';
import { Tooltip } from '@/components/ui';
import { Checkbox } from '@/components/ui';
import { HelpCircle } from 'lucide-react';
import { useFlatCategories } from '../../hooks/useCategories';
import { useUnitsByType } from '../../hooks/useUnits';
import { MarketType, MarketTypeLabels, type RawProductWithRelations } from '@bstock/shared';

/**
 * Contenu du tooltip explicatif pour le champ isBulk
 */
const BULK_TOOLTIP_CONTENT = (
  <div className="max-w-xs space-y-2 text-left whitespace-normal">
    <p className="font-semibold">Produit en vrac (Bulk)</p>
    <p>
      Cochez cette case pour les produits servis en vrac et dont le dosage
      varie naturellement :
    </p>
    <ul className="list-disc list-inside space-y-1 text-xs">
      <li>Huile de friture (absorption variable)</li>
      <li>Boissons fontaine (dosage machine)</li>
      <li>Cafe en grains (dosage moulin)</li>
      <li>Glace en bac (dosage a la cuillere)</li>
      <li>Sauces en distributeur</li>
    </ul>
    <p className="text-xs opacity-80">
      Ces produits generent des ecarts "normaux" entre theorique et reel.
      Ils seront analyses separement dans le rapport de marge.
    </p>
  </div>
);

const MARKET_TYPE_OPTIONS = Object.values(MarketType).map((type) => ({
  value: type,
  label: MarketTypeLabels[type],
}));

interface InfosTabProps {
  product: RawProductWithRelations;
  onChange: (field: string, value: unknown) => void;
}

export function InfosTab({ product, onChange }: InfosTabProps) {
  const { data: categories } = useFlatCategories();
  const { data: unitsByType } = useUnitsByType();
  // Nouveau type de marche en attente de confirmation (impact retroactif sur les rapports)
  const [pendingMarketType, setPendingMarketType] = useState<MarketType | null>(null);

  // Build category options
  const categoryOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.fullPath ?? cat.name,
  })) ?? [];

  // Build unit options
  const unitOptions = unitsByType
    ? [
        ...unitsByType.MASS?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [],
        ...unitsByType.VOLUME?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [],
        ...unitsByType.PIECE?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [],
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Informations generales */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4">
          Informations generales
        </h3>

        <div className="space-y-4">
          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Nom du produit
            </label>
            <EditableField
              value={product.name}
              onSave={(value) => onChange('name', value)}
              placeholder="Nom du produit"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Description
            </label>
            <EditableField
              value={product.description ?? ''}
              onSave={(value) => onChange('description', value || null)}
              placeholder="Description du produit..."
              multiline
            />
          </div>

          {/* Categorie et SKU */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                Categorie
              </label>
              <EditableSelect
                value={product.categoryId}
                options={categoryOptions}
                onSave={(value) => onChange('categoryId', value)}
              />
            </div>

            <div className="flex flex-col items-end">
              <label className="block text-sm font-medium text-neutral-600 mb-2">
                SKU
              </label>
              <code className="inline-block text-sm font-mono text-neutral-600 bg-neutral-100 px-3 py-2 rounded-lg">
                {product.sku}
              </code>
              <p className="text-xs text-neutral-500 mt-1">Non modifiable</p>
            </div>
          </div>

          {/* Type de marche */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Type de marche
            </label>
            <EditableSelect
              value={product.marketType}
              options={MARKET_TYPE_OPTIONS}
              onSave={(value) => {
                if (value !== product.marketType) setPendingMarketType(value as MarketType);
              }}
            />
            <p className="text-xs text-neutral-500 mt-1">
              Classe le produit dans le rapport de marge (food cost ou maintenance)
            </p>
          </div>

          {/* Code-barres */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Code-barres / EAN
            </label>
            <EditableField
              value={product.barcode ?? ''}
              onSave={(value) => onChange('barcode', value || null)}
              placeholder="EAN-13"
            />
          </div>
        </div>
      </div>

      {/* Unite de base */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4">
          Unite de base
        </h3>

        <div>
          <label className="block text-sm font-medium text-neutral-600 mb-2">
            Unite recette
          </label>
          <EditableSelect
            value={product.recipeUnitId}
            options={unitOptions}
            onSave={(value) => onChange('recipeUnitId', value)}
          />
        </div>
      </div>

      {/* Gestion de stock */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4">
          Gestion de stock
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Stock minimum
            </label>
            <EditableField
              value={product.minStock?.toString() ?? ''}
              onSave={(value) => onChange('minStock', value ? parseFloat(value) : null)}
              type="number"
              placeholder="Aucun"
            />
            <p className="text-xs text-neutral-500 mt-1">{product.recipeUnit.symbol}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-2">
              Stock maximum
            </label>
            <EditableField
              value={product.maxStock?.toString() ?? ''}
              onSave={(value) => onChange('maxStock', value ? parseFloat(value) : null)}
              type="number"
              placeholder="Aucun"
            />
            <p className="text-xs text-neutral-500 mt-1">{product.recipeUnit.symbol}</p>
          </div>
        </div>
      </div>

      {/* Classification Bulk */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-dark-500">
            Classification Bulk
          </h3>
          <Tooltip content={BULK_TOOLTIP_CONTENT} position="right" wide>
            <HelpCircle className="w-4 h-4 text-neutral-400 hover:text-neutral-600 cursor-help" />
          </Tooltip>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-neutral-700">Produit en vrac</span>
            <p className="text-xs text-neutral-500 mt-1">
              Ecarts analyses separement dans le rapport de marge
            </p>
          </div>
          <Checkbox
            checked={product.isBulk}
            onChange={(e) => onChange('isBulk', e.target.checked)}
          />
        </div>
      </div>

      <Modal
        isOpen={pendingMarketType !== null}
        onClose={() => setPendingMarketType(null)}
        title="Changer le type de marche"
        size="md"
      >
        <div className="px-6 py-4 space-y-3 text-sm text-neutral-700">
          <p>
            Passer <strong>{product.name}</strong> de <strong>{MarketTypeLabels[product.marketType]}</strong> a{' '}
            <strong>{pendingMarketType ? MarketTypeLabels[pendingMarketType] : ''}</strong> ?
          </p>
          <p>
            Le rapport de marge sera recalcule avec ce nouveau classement pour toutes les periodes, y compris
            passees, et pour tous les sites qui utilisent ce produit.
          </p>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setPendingMarketType(null)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onChange('marketType', pendingMarketType);
              setPendingMarketType(null);
            }}
          >
            Confirmer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
